import { atom, type SetStateAction, type WritableAtom } from 'jotai';
import type { FilterValueAtom } from '../components/filter-bar/types.ts';
import {
  fromSortSelectionString,
  type NullableFilterValues,
  type SortColumnsMap,
  type SortSelectionString,
} from '@pac/filter';

type FilterChangeHandler<TFilters extends Record<string, unknown>> =
  WritableAtom<
    null,
    [
      {
        prev: NullableFilterValues<TFilters>;
        next: NullableFilterValues<TFilters>;
        key?: keyof TFilters;
      },
    ],
    void
  >;

export function createFilterAtoms<TFilters extends Record<string, unknown>>(
  initialValues: NullableFilterValues<TFilters>,
  onChangeAtom?: FilterChangeHandler<TFilters>,
) {
  const baseValuesAtom = atom<NullableFilterValues<TFilters>>(initialValues);

  const valuesAtom = atom(
    (get) => get(baseValuesAtom),

    (get, set, update: SetStateAction<NullableFilterValues<TFilters>>) => {
      const prev = get(baseValuesAtom);

      const next =
        typeof update === 'function'
          ? (
              update as (
                prev: NullableFilterValues<TFilters>,
              ) => NullableFilterValues<TFilters>
            )(prev)
          : update;

      if (Object.is(prev, next)) return;

      set(baseValuesAtom, next);

      if (onChangeAtom) {
        set(onChangeAtom, { prev, next });
      }
    },
  );

  function valueAtom<K extends keyof TFilters>(
    key: K,
  ): FilterValueAtom<TFilters[K]> {
    return atom(
      (get) => get(baseValuesAtom)[key],

      (get, set, update: SetStateAction<NullableFilterValues<TFilters>[K]>) => {
        const prevValues = get(baseValuesAtom);
        const prev = prevValues[key];

        const next =
          typeof update === 'function'
            ? (
                update as (
                  prev: NullableFilterValues<TFilters>[K],
                ) => NullableFilterValues<TFilters>[K]
              )(prev)
            : update;

        if (Object.is(prev, next)) return;

        const nextValues = {
          ...prevValues,
          [key]: next,
        };

        set(baseValuesAtom, nextValues);

        if (onChangeAtom) {
          set(onChangeAtom, {
            prev: prevValues,
            next: nextValues,
            key,
          });
        }
      },
    );
  }

  return {
    valuesAtom,
    valueAtom,
  };
}

type SortChangeHandler<TSortColumns extends SortColumnsMap> = WritableAtom<
  null,
  [
    {
      prev: SortSelectionString<TSortColumns> | null;
      next: SortSelectionString<TSortColumns> | null;
    },
  ],
  void
>;

export function createSortAtom<TSortColumns extends SortColumnsMap>(
  sortColumns: TSortColumns,
  initialValue: SortSelectionString<TSortColumns> | null = null,
  onChangeAtom?: SortChangeHandler<TSortColumns>,
) {
  const normalizedInitialValue =
    initialValue &&
    fromSortSelectionString(sortColumns, initialValue) !== null
      ? initialValue
      : null;

  const baseSortAtom = atom<SortSelectionString<TSortColumns> | null>(
    normalizedInitialValue,
  );

  const sortAtom = atom(
    (get) => get(baseSortAtom),
    (
      get,
      set,
      update: SetStateAction<SortSelectionString<TSortColumns> | null>,
    ) => {
      const prev = get(baseSortAtom);
      const next =
        typeof update === 'function'
          ? (
              update as (
                prev: SortSelectionString<TSortColumns> | null,
              ) => SortSelectionString<TSortColumns> | null
            )(prev)
          : update;

      const normalizedNext =
        next && fromSortSelectionString(sortColumns, next) !== null
          ? next
          : normalizedInitialValue;

      if (Object.is(prev, normalizedNext)) return;

      set(baseSortAtom, normalizedNext);

      if (onChangeAtom) {
        set(onChangeAtom, { prev, next: normalizedNext });
      }
    },
  );

  return {
    sortAtom,
  };
}
